/**
 * AllOrdersPage Container Component
 *
 * Stateless page container that composes section components.
 * State management is handled by parent or via useOrdersRouterSearch hook.
 */

import { useMemo, useCallback, useState } from 'react';
import { Counter } from '@/components/ui/counter';
import { DataTableShortcuts } from '../../../DataTable/DataTableShortcuts';
import {
  FilterChipsList,
  buildFilterOptions,
  toChipGroups,
  type FilterCategory,
} from '@components/filters';
import { OrdersToolbar } from '../Sections/OrdersToolbar';
import { OrdersTable } from '../Sections/OrdersTable';
import { OrdersCardList } from '../Sections/OrdersCardList';
import { OrderDetailSheet } from '../Sections/OrderDetailSheet';
import { useTranslation } from '@hooks/useTranslation';
import type { AllOrdersPageProps } from '../types';
import { ORDER_STATUS_FILTER_VALUES } from '../types';

export type { AllOrdersPageProps };

const DEFAULT_ORDERS: never[] = [];
const DEFAULT_SALESPERSON_IDS: string[] = [];
const DEFAULT_ORDER_STATUSES: string[] = [];

export function AllOrdersPage({
  totalCount = 0,
  salespersons,
  selectedSalespersonIds = DEFAULT_SALESPERSON_IDS,
  onSalespersonChange,
  selectedOrderStatuses = DEFAULT_ORDER_STATUSES,
  onOrderStatusesChange,
  onNewOrder,
  translations,
  isLoading = false,
  isFetching = false,
  orders = DEFAULT_ORDERS,
  onOrderIdClick,
  selectedOrderId: controlledSelectedOrderId,
  onSelectedOrderChange,
  currentUser,
  pagination,
}: AllOrdersPageProps) {
  const {t} = useTranslation();

  const [internalSelectedOrderId, setInternalSelectedOrderId] = useState<number | null>(null);
  const isControlled = controlledSelectedOrderId !== undefined;
  const selectedOrderId = isControlled ? controlledSelectedOrderId : internalSelectedOrderId;
  const setSelectedOrderId = isControlled
    ? (id: number | null) => onSelectedOrderChange?.(id)
    : setInternalSelectedOrderId;
  const handleOrderIdClick = useCallback(
    (orderId: number) => {
      setSelectedOrderId(orderId);
      onOrderIdClick?.(orderId);
    },
    [onOrderIdClick, setSelectedOrderId] // eslint-disable-line react-hooks/exhaustive-deps -- setSelectedOrderId changes identity in controlled mode
  );

  const title = t('title');

  const categories = useMemo<FilterCategory[]>(() => [
    {
      key: 'salesperson',
      label: t('toolbar.filter_categories.salesperson'),
      options: salespersons.map((sp) => ({value: String(sp.id), label: sp.name})),
      selected: selectedSalespersonIds,
      onChange: onSalespersonChange,
      searchable: true,
    },
    {
      key: 'order_status',
      label: t('toolbar.filter_categories.order_status'),
      options: buildFilterOptions(t, 'toolbar.order_statuses', ORDER_STATUS_FILTER_VALUES),
      selected: selectedOrderStatuses,
      onChange: onOrderStatusesChange,
    },
  ], [
    t,
    salespersons,
    selectedSalespersonIds,
    onSalespersonChange,
    selectedOrderStatuses,
    onOrderStatusesChange,
  ]);

  const chipGroups = useMemo(() => toChipGroups(categories), [categories]);

  const countText = isLoading ? '...' : totalCount.toLocaleString();

  return (
    <>
      <DataTableShortcuts>
        <div className="flex flex-col gap-4 h-full min-h-0 px-6 sm:px-8 lg:px-16">
          <div className="hidden md:flex flex-wrap items-center justify-between gap-4 flex-shrink-0">
            <div className="hidden md:flex items-center gap-3">
              <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
                {title}
              </h1>
              <Counter value={countText} data-loading={isLoading || isFetching} />
            </div>

            <OrdersToolbar
              categories={categories}
              onNewOrder={onNewOrder}
              translations={translations?.toolbar}
              className="hidden md:block"
            />
          </div>

          <FilterChipsList groups={chipGroups} />

          <OrdersTable
            orders={orders}
            isLoading={isLoading}
            isFetching={isFetching}
            onOrderIdClick={handleOrderIdClick}
            currentUser={currentUser}
            className="flex-1 min-h-0 hidden md:flex"
          />

          <OrdersCardList
            orders={orders}
            isLoading={isLoading}
            isFetching={isFetching}
            onOrderIdClick={handleOrderIdClick}
            pagination={pagination}
            translations={translations?.table}
            className="pb-4 flex-1 min-h-0 md:hidden"
          />
        </div>
      </DataTableShortcuts>

      <OrderDetailSheet
        orderId={selectedOrderId}
        open={selectedOrderId !== null}
        onOpenChange={(open) => { if (!open) setSelectedOrderId(null); }}
        currentUser={currentUser}
      />
    </>
  );
}
